/**
 * <Glow> — la lueur d'accueil (molecule, Brand core V16 chap. 03 « Les fonds »).
 *
 * « Une seule lumière est permise : une lueur jaune très légère, qui entre par le coin supérieur
 * droit. » Le livre la chiffre en CSS — `radial-gradient(62% 48% at 100% 0%, …)` — et le natif
 * n'a pas de `radial-gradient` : on la dessine donc en SVG, une ellipse centrée sur le coin
 * haut-droit, remplie d'un dégradé radial rapporté à SA boîte. Rapporté à la boîte de l'ellipse,
 * le cercle unité d'un dégradé radial devient l'ellipse elle-même : les arrêts tombent le long du
 * même rayon que ceux du CSS, sans rien mesurer. `react-native-svg` rend aussi sur le web, donc
 * une seule implémentation.
 *
 * Elle remplit son parent en absolu, sous tout le reste, et ne capte aucun toucher. Ce n'est PAS
 * à une page de la poser : c'est la **coquille** qui la peint (`<ScreenBackground glow>`), pour
 * qu'elle parte du coin où vit le header et passe sous lui comme sous la page.
 *
 *  - ✅ l'accueil de l'espace (le livre l'accorde aussi à la connexion et à la réussite) ;
 *  - ❌ une liste, un formulaire, des réglages ; jamais deux lueurs sur un écran.
 *
 * **Sur la nuit, elle n'est pas montée du tout** : « en sombre, le fond reste uni ». Pas par une
 * couleur transparente : `react-native-svg` natif jette l'alpha de la couleur d'un arrêt et ne
 * garde que `stopOpacity` (`extractGradient.ts`) — un jaune « à 0 % » y ressortait à 16 %. Pas
 * non plus par un nombre du thème : sur le web, Unistyles ne recalcule pas une feuille quand
 * seul un nombre change. Le nom du thème, lui, se lit pareil des deux côtés (`useIsDarkTheme`).
 * Pour la même raison, l'opacité d'un arrêt ne passe QUE par `stopOpacity`, sur un jaune opaque.
 */
import React, { useId } from "react"
import { StyleSheet as RNStyleSheet, View } from "react-native"
import Svg, { Defs, Ellipse, RadialGradient, Stop } from "react-native-svg"
import { useUnistyles } from "react-native-unistyles"

import { useIsDarkTheme } from "../../hooks/useIsDarkTheme"
import { glow } from "../../theme/tokens/effects"

export type GlowProps = {
    testID?: string
}

/** Un pourcentage SVG — `rx` se lit sur la largeur de la boîte, `ry` sur sa hauteur. */
const percent = (fraction: number) => `${fraction * 100}%`

export function Glow(props: GlowProps) {
    const { testID } = props
    const { theme } = useUnistyles()
    const night = useIsDarkTheme()
    // Un identifiant par instance : sur le web, les dégradés partagent l'espace de noms du
    // document. `useId` rend des deux-points, qu'une référence `url(#…)` ne lit pas.
    const gradientId = `glow-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`

    if (night) return null

    return (
        <View testID={testID} style={RNStyleSheet.absoluteFill} pointerEvents="none">
            <Svg width="100%" height="100%">
                <Defs>
                    <RadialGradient id={gradientId} cx="50%" cy="50%" r="50%">
                        {glow.stops.map((offset, index) => (
                            <Stop
                                key={offset}
                                offset={offset}
                                stopColor={theme.colors.glow}
                                stopOpacity={glow.alphas[index]}
                            />
                        ))}
                    </RadialGradient>
                </Defs>
                <Ellipse
                    cx="100%"
                    cy="0%"
                    rx={percent(glow.radiusX)}
                    ry={percent(glow.radiusY)}
                    fill={`url(#${gradientId})`}
                />
            </Svg>
        </View>
    )
}
